import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  input,
  OnDestroy,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NgIcon } from '@ng-icons/core';
import { loadTicketPdfPreview } from '../../domain/ticket-document-preview';
import {
  loadTicketSpreadsheetPreview,
  TicketSpreadsheetPreviewData,
} from '../../domain/ticket-spreadsheet-preview';

export interface TicketAttachmentPreview {
  readonly kind: 'image' | 'pdf' | 'spreadsheet';
  readonly name: string;
  readonly originalUrl: string;
  readonly imageUrl?: string;
  readonly pdfUrl?: SafeResourceUrl;
  readonly spreadsheet?: TicketSpreadsheetPreviewData;
}

@Component({
  selector: 'app-ticket-attachment-preview',
  standalone: true,
  imports: [CommonModule, NgIcon],
  templateUrl: './ticket-attachment-preview.component.html',
  styleUrl: './ticket-attachment-preview.component.scss',
})
export class TicketAttachmentPreviewComponent implements OnDestroy {
  readonly downloadLabel = input('Download');
  readonly closeLabel = input('Close preview');
  readonly loadingLabel = input('Loading preview…');
  readonly sheetLabel = input('Sheet');
  readonly emptySheetLabel = input('This sheet is empty.');
  readonly truncatedLabel = input(
    'Preview limited to the first 500 rows and 50 columns. Download the file to view all data.',
  );
  readonly failed = output<void>();
  readonly closed = output<void>();

  @ViewChild('dialog') private dialog?: ElementRef<HTMLElement>;

  readonly preview = signal<TicketAttachmentPreview | null>(null);
  readonly loading = signal(false);
  readonly selectedSheetIndex = signal(0);
  readonly selectedSheet = computed(() => {
    const sheets = this.preview()?.spreadsheet?.sheets ?? [];
    return sheets[this.selectedSheetIndex()] ?? null;
  });

  private readonly sanitizer = inject(DomSanitizer);
  private abortController?: AbortController;
  private pdfObjectUrl?: string;
  private ownedOriginalUrl?: string;
  private trigger?: HTMLElement;

  isOpen(): boolean {
    return this.preview() !== null;
  }

  openImage(name: string, url: string, trigger?: HTMLElement): void {
    this.prepare(trigger);
    this.preview.set({
      kind: 'image',
      name,
      originalUrl: url,
      imageUrl: url,
    });
    this.focus();
  }

  async openPdf(name: string, url: string, trigger?: HTMLElement): Promise<void> {
    this.prepare(trigger);
    const controller = new AbortController();
    this.abortController = controller;
    this.loading.set(true);
    this.preview.set({ kind: 'pdf', name, originalUrl: url });
    this.focus();

    try {
      const blob = await loadTicketPdfPreview(url, controller.signal);
      if (controller.signal.aborted) return;
      const objectUrl = URL.createObjectURL(blob);
      this.pdfObjectUrl = objectUrl;
      this.preview.update((preview) =>
        preview
          ? {
              ...preview,
              pdfUrl: this.sanitizer.bypassSecurityTrustResourceUrl(objectUrl),
            }
          : preview,
      );
    } catch {
      if (controller.signal.aborted) return;
      this.close(false);
      this.failed.emit();
    } finally {
      if (this.abortController === controller) {
        this.abortController = undefined;
        this.loading.set(false);
      }
    }
  }

  async openSpreadsheetFile(file: File, trigger?: HTMLElement): Promise<void> {
    this.prepare(trigger);
    const originalUrl = URL.createObjectURL(file);
    this.ownedOriginalUrl = originalUrl;
    await this.loadSpreadsheet(file.name, originalUrl, file);
  }

  async openSpreadsheetUrl(
    name: string,
    url: string,
    trigger?: HTMLElement,
  ): Promise<void> {
    this.prepare(trigger);
    await this.loadSpreadsheet(name, url, url);
  }

  selectSheet(index: number): void {
    this.selectedSheetIndex.set(index);
  }

  close(restoreFocus = true): void {
    const wasOpen = this.isOpen();
    const trigger = this.trigger;
    this.trigger = undefined;
    this.abortController?.abort();
    this.abortController = undefined;
    if (this.pdfObjectUrl) {
      URL.revokeObjectURL(this.pdfObjectUrl);
      this.pdfObjectUrl = undefined;
    }
    if (this.ownedOriginalUrl) {
      URL.revokeObjectURL(this.ownedOriginalUrl);
      this.ownedOriginalUrl = undefined;
    }
    this.loading.set(false);
    this.selectedSheetIndex.set(0);
    this.preview.set(null);
    if (wasOpen) this.closed.emit();
    if (restoreFocus && trigger?.isConnected) {
      setTimeout(() => trigger.focus());
    }
  }

  ngOnDestroy(): void {
    this.close(false);
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: Event): void {
    if (!this.isOpen()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.close();
  }

  private prepare(trigger?: HTMLElement): void {
    this.close(false);
    this.trigger = trigger;
  }

  private async loadSpreadsheet(
    name: string,
    originalUrl: string,
    source: Blob | string,
  ): Promise<void> {
    const controller = new AbortController();
    this.abortController = controller;
    this.loading.set(true);
    this.selectedSheetIndex.set(0);
    this.preview.set({ kind: 'spreadsheet', name, originalUrl });
    this.focus();

    try {
      const spreadsheet = await loadTicketSpreadsheetPreview(
        source,
        controller.signal,
      );
      if (controller.signal.aborted) return;
      this.preview.update((preview) =>
        preview ? { ...preview, spreadsheet } : preview,
      );
    } catch {
      if (controller.signal.aborted) return;
      this.close(false);
      this.failed.emit();
    } finally {
      if (this.abortController === controller) {
        this.abortController = undefined;
        this.loading.set(false);
      }
    }
  }

  private focus(): void {
    setTimeout(() => this.dialog?.nativeElement.focus());
  }
}
